import React, { useCallback, useMemo } from 'react';
import { useAssetTypes } from '@components/v2/AssetTypeSelector/hooks/useAssets';
import { Loader } from '@ui';
import { Checkbox } from '@ui/molecules/inputs/Checkbox';
import { translate } from '@utils/i18n';
import useAIInspectionManager from '../../hooks/useAIInspectionManager';
import useUpdateAIInspectionManager from '../../hooks/useUpdateAIInspectionManager';
import './UnansweredQuestionsTabStyles.scss';
import AssetExclusionsManager from './AssetExclusionsManager';
const namespace = 'features.settings.ai_inspection_manager.unanswered_questions';
function UnansweredQuestionsTab() {
    const { data: settings, isLoading } = useAIInspectionManager();
    const { updateSettings } = useUpdateAIInspectionManager();
    const advancedSettings = useMemo(() => {
        var _a, _b;
        const apiSettings = settings === null || settings === void 0 ? void 0 : settings.aiInspectionManagerUnansweredQuestionsAdvancedSettings;
        return {
            includeInHighImpactIssues: (_a = apiSettings === null || apiSettings === void 0 ? void 0 : apiSettings.includeInHighImpactIssues) !== null && _a !== void 0 ? _a : false,
            assetExclusions: (_b = apiSettings === null || apiSettings === void 0 ? void 0 : apiSettings.assetExclusions) !== null && _b !== void 0 ? _b : []
        };
    }, [settings === null || settings === void 0 ? void 0 : settings.aiInspectionManagerUnansweredQuestionsAdvancedSettings]);
    const handleUpdateSettings = useCallback((updatedSettings) => {
        if (!settings)
            return;
        updateSettings.mutate({
            settingsUpdate: Object.assign(Object.assign({}, settings), { aiInspectionManagerUnansweredQuestionsAdvancedSettings: updatedSettings })
        });
    }, [settings, updateSettings]);
    const handleCheckboxChange = useCallback((field, value) => {
        handleUpdateSettings(Object.assign(Object.assign({}, advancedSettings), { [field]: value }));
    }, [advancedSettings, handleUpdateSettings]);
    const { data: assetTypes = [], isLoading: assetTypesLoading, isError: assetTypesError } = useAssetTypes();
    // Create lookup map for asset type labels
    const assetTypeLabelMap = useMemo(() => {
        const map = {};
        assetTypes.forEach((type) => {
            var _a;
            map[type.value] = type.label;
            (_a = type.items) === null || _a === void 0 ? void 0 : _a.forEach((item) => {
                map[item.value] = item.label;
            });
        });
        return map;
    }, [assetTypes]);
    const handleAddAssets = useCallback((assetTypeValues) => {
        const newExclusions = assetTypeValues.map((value) => ({
            value,
            label: assetTypeLabelMap[value] || value
        }));
        const updatedExclusions = [...advancedSettings.assetExclusions, ...newExclusions];
        handleUpdateSettings(Object.assign(Object.assign({}, advancedSettings), { assetExclusions: updatedExclusions }));
    }, [advancedSettings, handleUpdateSettings, assetTypeLabelMap]);
    const handleDeleteAsset = useCallback((assetTypeValue) => {
        const updatedExclusions = advancedSettings.assetExclusions.filter((a) => a.value !== assetTypeValue);
        handleUpdateSettings(Object.assign(Object.assign({}, advancedSettings), { assetExclusions: updatedExclusions }));
    }, [advancedSettings, handleUpdateSettings]);
    const handleDeleteAssets = useCallback((assetTypeValues) => {
        const updatedExclusions = advancedSettings.assetExclusions.filter((a) => !assetTypeValues.includes(a.value));
        handleUpdateSettings(Object.assign(Object.assign({}, advancedSettings), { assetExclusions: updatedExclusions }));
    }, [advancedSettings, handleUpdateSettings]);
    if (isLoading || assetTypesLoading) {
        return (React.createElement("div", { style: { display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' } },
            React.createElement(Loader, { size: "48", title: "Loading", vertical: true })));
    }
    if (assetTypesError) {
        return (React.createElement("div", { className: "unanswered-questions-tab__loading" }, translate('error_loading_assets', {
            namespace: 'features.settings.ai_inspection_manager.missing_information'
        })));
    }
    return (React.createElement("div", { className: "unanswered-questions-tab main-section-container" },
        React.createElement("div", { className: "advanced-settings-section" },
            React.createElement("h3", { className: "advanced-settings-section__title" },
                translate('section_title', { namespace }),
                React.createElement("div", { className: "advanced-settings-section__title-line" })),
            React.createElement("div", { className: "advanced-settings-section__checkbox" },
                React.createElement(Checkbox, { id: "include-high-impact-unanswered-questions", label: translate('include_in_high_impact', { namespace }), checked: advancedSettings.includeInHighImpactIssues, onChange: (checked) => handleCheckboxChange('includeInHighImpactIssues', checked) }))),
        React.createElement("div", { className: "advanced-settings-section" },
            React.createElement("h3", { className: "advanced-settings-section__title" }, translate('asset_exclusion', { namespace })),
            React.createElement("div", { className: "advanced-settings-section__description" }, translate('asset_exclusion_description', { namespace })),
            React.createElement(AssetExclusionsManager, { assetExclusions: advancedSettings.assetExclusions, onAddAssets: handleAddAssets, onDeleteAsset: handleDeleteAsset, onDeleteAssets: handleDeleteAssets, subtitle: translate('asset_exclusion_description', { namespace }) }))));
}
export default UnansweredQuestionsTab;
